<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale, setLocale } = useI18n()
const route = useRoute()

withDefaults(
  defineProps<{
    variant?: 'icon' | 'text'
  }>(),
  {
    variant: 'icon',
  },
)

// Hardcode languages since Gallery uses:
// en, ja, fr, it
const items = [
  { code: 'en', name: 'EN' },
  { code: 'ja', name: '日本語' },
  { code: 'fr', name: 'FR' },
  { code: 'it', name: 'IT' },
] as const

type LocaleCode = (typeof items)[number]['code']
const isLocaleCode = (code: string): code is LocaleCode =>
  items.some((item) => item.code === code)

const open = ref(false)
const rootRef = ref<HTMLElement | null>(null)

const selectLocale = async (code: string) => {
  if (!isLocaleCode(code)) return
  await setLocale(code)
  open.value = false
}

const closeMenu = () => {
  open.value = false
}

const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node | null
  if (!target) return
  if (!rootRef.value?.contains(target)) {
    closeMenu()
  }
}

watch(
  () => route.fullPath,
  () => closeMenu(),
)

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
})
</script>

<template>
  <div
    ref="rootRef"
    class="lang">
    <button
      class="lang-btn"
      :class="{ 'lang-btn--text': variant === 'text' }"
      @click="open = !open"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="`Language: ${String(locale).toUpperCase()}`">
      <svg
        v-if="variant === 'icon'"
        viewBox="0 0 24 24"
        width="40"
        height="40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          stroke-width="1.5" />
        <path
          d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18"
          stroke="currentColor"
          stroke-width="1.5" />
      </svg>
      <span
        v-else
        class="lang-btn-text">
        {{ String(locale).toUpperCase() }}
      </span>
    </button>

    <ul
      v-if="open"
      class="lang-menu">
      <li
        v-for="l in items"
        :key="l.code">
        <button
          class="lang-item"
          @click="selectLocale(l.code)">
          {{ l.name }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.lang {
  position: relative;
  display: inline-block;
}

.lang-btn {
  background: none;
  border: none;
  border-radius: 999px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color);
}

.lang-btn--text {
  width: auto;
  min-width: 44px;
  padding: 0 0.75rem;
  border: 1px solid color-mix(in srgb, var(--text-color) 24%, transparent);
}

.lang-btn-text {
  font-family: var(--title-font);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1;
}

.lang-menu {
  position: absolute;
  right: 0;
  margin-top: 8px;
  padding: 6px;
  background-color: var(--background-color);
  border: 1px solid #ddd;
  border-radius: 8px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  z-index: var(--z-dropdown);
}

.lang-item {
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 6px 8px;
  color: var(--text-color);
  cursor: pointer;
}

.lang-item:hover {
  background: var(--content-background);
}
</style>
